<script lang="ts" setup>
  import BannerAndyRuiz from '~/components/loggedOutNew/BannerAndyRuiz.vue';
  import CAFRAFSlider from '~/components/loggedOutNew/CAFRAFSlider.vue';

  interface ISliderItem {
    id: string;
    image: string;
    title: string;
    lead: string;
    className?: string;
    component?: any;
    type?: string;
  }

  const emit = defineEmits<{ click: [] }>();

  const { activeBreakpoint } = useDeviceType();
  const isMobile = computed(() => ['xxs', 'xs', 'sm'].includes(activeBreakpoint.value));
  const isTablet = computed(() => ['md'].includes(activeBreakpoint.value));
  const items = shallowRef<ISliderItem[]>([]);

  watch(activeBreakpoint, setItems, { immediate: true });

  function setItems() {
    const suffix = isMobile.value ? 'mobile' : 'desktop';

    items.value = [
      {
        id: 'andy-ruiz-banner',
        type: 'component',
        component: BannerAndyRuiz,
      },
      {
        id: 'caf-banner',
        image: `/images/lo-hp-new/caf-art-${suffix}.webp`,
        title: 'Challenge friends',
        lead: 'This social feature allows players to add friends, create private game rooms, and compete head-to-head.',
        className:
          'lg:self-end w-full max-w-[225px] desktop-1536:max-w-[415px] object-contain absolute right-0 bottom-0',
      },
      {
        id: 'raf-banner',
        image: `/images/lo-hp-new/raf-image-${suffix}.webp`,
        title: 'Refer a Friend',
        lead: 'Bring your friends to the MaxDuel platform and earn extra rewards. The more friends you refer, the more benefits you can unlock!',
      },
    ] as ISliderItem[];
  }
</script>

<template>
  <section class="@container/caf-raf w-full relative rounded-[10px]">
    <div v-if="isMobile" class="block md:hidden">
      <!-- mobile view -->
      <UCarousel
        v-slot="{ item }"
        :items="items"
        :ui="{ item: 'basis-5/6', container: 'items-stretch' }"
        loop
        align="start"
      >
        <component :is="item.component" v-if="item.type === 'component'" :key="`${item.id}-component`" />
        <div
          v-else
          :key="item.id"
          class="flex flex-col justify-evenly gap-6 px-6 py-10 rounded-[10px] min-w-[280px] h-full bg-gradient-to-r from-[#0874BF] via-[#0257A5] to-[#053A8D] bg-opacity-[0.05]"
          @click="emit('click')"
        >
          <NuxtImg :src="item.image" alt="" class="w-full -mb-6" fit="cover" format="webp" preload densities="x1 x2" />
          <h2 class="text-left text-white font-barlow text-[42px] font-extrabold leading-[110%] uppercase">
            {{ item.title }}
          </h2>
          <p class="text-left text-white font-barlow text-base font-medium leading-[150%]">
            {{ item.lead }}
          </p>
        </div>
      </UCarousel>
    </div>
    <div v-else-if="isTablet" class="hidden md:block lg:hidden">
      <!-- tablet view -->
      <UCarousel
        v-slot="{ item }"
        :items="items"
        :ui="{ item: 'basis-5/6', container: 'items-stretch' }"
        loop
        align="start"
      >
        <component :is="item.component" v-if="item.type === 'component'" :key="item.id" />
        <div
          v-else
          class="flex rounded-[10px] h-full bg-gradient-to-r from-[#0874BF] via-[#0257A5] to-[#053A8D] bg-opacity-[0.05]"
          @click="emit('click')"
        >
          <div class="flex flex-col basis-3/5 gap-4 justify-center px-6 py-10">
            <h2 class="text-left text-white font-barlow text-[40px] font-extrabold leading-[110%] uppercase">
              {{ item.title }}
            </h2>
            <p class="text-left text-white font-barlow text-base font-medium leading-[150%]">
              {{ item.lead }}
            </p>
          </div>

          <div v-if="item.id === 'caf-banner'" class="flex items-end-safe">
            <NuxtImg
              :src="item.image"
              alt=""
              class="object-contain"
              fit="contain"
              format="webp"
              preload
              densities="x1 x2"
            />
          </div>
          <div v-if="item.id === 'raf-banner'" class="flex basis-3/5 pt-4 pr-2">
            <NuxtImg :src="item.image" alt="" class="object-contain" fit="contain" format="webp" preload />
          </div>
        </div>
      </UCarousel>
    </div>
    <div v-else class="hidden lg:block">
      <!-- desktop view -->
      <div class="grid grid-cols-2 gap-4">
        <CAFRAFSlider class="aspect-[280/436] max-h-[34.375rem] h-auto min-[45rem]:aspect-[716/372]" />
        <BannerAndyRuiz class="aspect-[280/436] max-h-[34.375rem] h-auto min-[45rem]:aspect-[716/372]" />
      </div>
    </div>
  </section>
</template>
